import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PxMark } from "@/components/PxMark";

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

const signupSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
  displayName: z.string().min(2, "Le nom doit contenir au moins 2 caractères").optional(),
});

/** Cellule de la trame : carré 8px + 8px de blanc tournant. */
const CELL = 16;

/**
 * Le seul spectacle autorisé de l'app : trame statique de carrés 8px
 * en surface-3 (CSS pur), où un unique carré rose se déplace d'une
 * cellule toutes les ~2s. Statique si prefers-reduced-motion.
 */
function PixelTrame() {
  const paneRef = useRef<HTMLDivElement>(null);
  const [cell, setCell] = useState({ col: 8, row: 10 });

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const id = setInterval(() => {
      const pane = paneRef.current;
      if (!pane) return;
      const cols = Math.max(1, Math.floor(pane.clientWidth / CELL));
      const rows = Math.max(1, Math.floor(pane.clientHeight / CELL));
      setCell((prev) => {
        const moves = [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ];
        const [dc, dr] = moves[Math.floor(Math.random() * moves.length)];
        return {
          col: Math.min(cols - 1, Math.max(0, prev.col + dc)),
          row: Math.min(rows - 1, Math.max(0, prev.row + dr)),
        };
      });
    }, 2000);

    return () => clearInterval(id);
  }, []);

  return (
    <div
      ref={paneRef}
      aria-hidden="true"
      className="relative hidden overflow-hidden border-r border-border lg:block lg:w-1/2"
      style={{
        // Carré 8px surface-3 en haut-gauche de chaque cellule 16px (CSS pur).
        backgroundImage:
          "conic-gradient(from 0deg at 8px 8px, transparent 0deg 270deg, hsl(var(--surface-3)) 270deg 360deg)",
        backgroundSize: `${CELL}px ${CELL}px`,
      }}
    >
      <span
        className="absolute h-2 w-2 bg-brand"
        style={{ left: cell.col * CELL, top: cell.row * CELL }}
      />
    </div>
  );
}

export default function Auth() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [isLoading, setIsLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupDisplayName, setSignupDisplayName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { user, isLoading: authLoading, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      const returnUrl = location.state?.returnUrl || "/";
      navigate(returnUrl, { replace: true });
    }
  }, [user, authLoading, navigate, location]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validation = loginSchema.safeParse({
      email: loginEmail,
      password: loginPassword,
    });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[`login_${err.path[0]}`] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await signIn(loginEmail, loginPassword);
    setIsLoading(false);

    if (error) {
      const errorMessage = error.message.includes("Invalid login credentials")
        ? "Email ou mot de passe incorrect"
        : error.message;
      toast.error("Erreur de connexion", { description: errorMessage });
    } else {
      toast.success("Connexion réussie", { description: "Bienvenue !" });
      navigate("/");
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validation = signupSchema.safeParse({
      email: signupEmail,
      password: signupPassword,
      displayName: signupDisplayName || undefined,
    });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[`signup_${err.path[0]}`] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await signUp(signupEmail, signupPassword, signupDisplayName);
    setIsLoading(false);

    if (error) {
      let errorMessage = error.message;
      if (error.message.includes("User already registered")) {
        errorMessage = "Un compte existe déjà avec cet email";
      }
      toast.error("Erreur d'inscription", { description: errorMessage });
    } else {
      toast.success("Compte créé", { description: "Votre compte a été créé avec succès !" });
      navigate("/");
    }
  };

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <PxMark state="thinking" size={24} />
      </div>
    );
  }

  const isLogin = mode === "login";

  return (
    <div className="flex min-h-screen bg-background">
      <PixelTrame />

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-[400px]">
          <div className="flex items-center gap-3">
            <PxMark size={32} state="idle" label="Pixel" />
            <span className="text-xl font-semibold text-foreground">pixel</span>
          </div>

          <p className="mt-6 font-display text-2xl italic text-foreground">
            Décris-la. Elle existe.
          </p>

          <div className="mt-8 border border-hairline-strong bg-card p-6">
            {isLogin ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-[13px] font-medium text-foreground">
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-secondary"
                    disabled={isLoading}
                  />
                  {errors.login_email && (
                    <p className="font-mono text-[12px] text-err">{errors.login_email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-[13px] font-medium text-foreground">
                    Mot de passe
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="bg-secondary"
                    disabled={isLoading}
                  />
                  {errors.login_password && (
                    <p className="font-mono text-[12px] text-err">{errors.login_password}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <PxMark state="generating" size={14} className="mr-2" />
                      Connexion…
                    </>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name" className="text-[13px] font-medium text-foreground">
                    Nom (optionnel)
                  </Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Votre nom"
                    value={signupDisplayName}
                    onChange={(e) => setSignupDisplayName(e.target.value)}
                    className="bg-secondary"
                    disabled={isLoading}
                  />
                  {errors.signup_displayName && (
                    <p className="font-mono text-[12px] text-err">{errors.signup_displayName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="text-[13px] font-medium text-foreground">
                    Email
                  </Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="bg-secondary"
                    disabled={isLoading}
                  />
                  {errors.signup_email && (
                    <p className="font-mono text-[12px] text-err">{errors.signup_email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password" className="text-[13px] font-medium text-foreground">
                    Mot de passe
                  </Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="bg-secondary"
                    disabled={isLoading}
                  />
                  {errors.signup_password && (
                    <p className="font-mono text-[12px] text-err">{errors.signup_password}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <PxMark state="generating" size={14} className="mr-2" />
                      Création…
                    </>
                  ) : (
                    "Créer un compte"
                  )}
                </Button>
              </form>
            )}

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setMode(isLogin ? "signup" : "login");
                  setErrors({});
                }}
                className="text-[13px] text-muted-foreground underline-offset-[3px] transition-colors hover:text-foreground hover:underline"
                disabled={isLoading}
              >
                {isLogin ? "Pas de compte ? Créer un compte" : "Déjà un compte ? Se connecter"}
              </button>
            </div>
          </div>

          <p className="mt-6 text-center font-mono text-[11px] text-faint">
            En vous inscrivant, vous acceptez nos conditions d'utilisation.
          </p>
        </div>
      </div>
    </div>
  );
}
